import { Flex, Text, ThemeIcon } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IParentChildItem, IPNSProps } from "@mctypes/global.types";
import { IconAlertCircle } from "@tabler/icons-react";
import React, { useEffect, useMemo, useState } from "react";
import { Prompt } from "react-router";
import { FailedAlert, ParentChildChart } from "../../../components/";
import HierarchyEditor from "../../../components/HierarchyEditor/HierarchyEditor";
import PNSExcelImportWithSpecTable from "../../../components/PNSExcelImport/PNSExcelImportWithSpecTable";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { MCApplicationsUrlEnum } from "../../../mc-shared/sharedConstants";
import { updateProjectPnsAndSetInRedux } from "../../actions";
import { HierarchyNode } from "../../hierarchyNodeUtils";
import { doLogUsage } from "../admin/restQueries";
import { formatHierarchyNodesToParentChildItems } from "./pnsUtils";

const PNS: React.FC<{ viewerMode?: boolean }> = ({ viewerMode = false }) => {
  const [nodes, setNodes] = useState<IParentChildItem[]>();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [showFailedAlert, setShowFailedAlert] = useState<boolean>(false);
  const [displayFullScreen, setDisplayFullScreen] = useState<boolean>(false);
  const project = useAppSelector((state) => state.adminReducer.project);
  const dispatch = useAppDispatch();
  const [importedData, setImportedData] = useState<HierarchyNode<IPNSProps>>(null);

  const pnsRootNode = useMemo<HierarchyNode<IPNSProps>>(() => {
    if (importedData != null) {
      return importedData;
    }

    if (project.pns == null || project.pns.length === 0) {
      return new HierarchyNode<IPNSProps>({
        _id: null,
        props: {
          pnsId: "root",
          title: "root",
        },
        parent: null,
        children: [],
      });
    }

    return HierarchyNode.createFromFlatList(project.pns);
  }, [project.pns, importedData]);

  useEffect(() => {
    try {
      if (pnsRootNode == null) {
        setNodes([]);
        return;
      }

      const _nodes = formatHierarchyNodesToParentChildItems(pnsRootNode);
      setNodes(_nodes.length > 0 ? _nodes : null);
    } catch (error) {
      setShowFailedAlert(true);
    }
  }, [pnsRootNode]);

  useEffect(() => {
    doLogUsage(project, MCApplicationsUrlEnum.PNS, "open");
  }, []);

  const saveStructure = async (rootNode: HierarchyNode<IPNSProps>) => {
    // check for duplicate pnsIds
    const pnsIds = new Set<string>();
    let hasDuplicate = false;
    for (const node of rootNode.forEach()) {
      if (pnsIds.has(node.props.pnsId)) {
        hasDuplicate = true;
        break;
      }
      pnsIds.add(node.props.pnsId);
    }

    if (hasDuplicate) {
      // use mantine notification to show error about duplicate pnsIds
      notifications.show({
        title: "Feil",
        message: (
          <>
            <Flex align="center">
              <ThemeIcon styles={{ root: { border: 0 } }} size={80} variant="outline" color="red">
                <IconAlertCircle />
              </ThemeIcon>
              <Text>
                Det finnes duplikate PNS IDer i strukturen. Vennligst sørg for at alle PNS IDer er unike før du lagrer.
              </Text>
            </Flex>
          </>
        ),
        color: "red",
      });
      return;
    }
    try {
      await dispatch(updateProjectPnsAndSetInRedux(rootNode.flatten()));
      dispatch(setShowSuccessMessageThunk("PNS lagret"));
      setIsDirty(false);
    } catch (error) {
      setShowFailedAlert(true);
    }
  };

  return (
    <>
      {showFailedAlert === true && <FailedAlert errorMessage="Failed to start PNS app" />}
      <Prompt when={isDirty === true} message={"Du har ulagrede endringer, er du sikker på at du vil forlate siden?"} />
      <div className="d-flex h-100">
        <div
          hidden={displayFullScreen === true || viewerMode === true}
          className="pr-2 w-50"
          style={{ borderRight: "1px solid #d3d3d3" }}
        >
          <HierarchyEditor
            onNodeChange={() => setIsDirty(true)}
            rootNode={pnsRootNode}
            onSave={(rootNode) => {
              saveStructure(rootNode);
              setIsDirty(false);
            }}
            importButton={
              // <PNSExcelImport
              //   hasImportedData={importedData != null}
              //   onReset={() => {
              //     setImportedData(null);
              //     setIsDirty(false);
              //   }}
              //   onValidated={(pnsTree) => {
              //     setIsDirty(true);
              //     setImportedData(pnsTree);
              //   }}
              // />
              <PNSExcelImportWithSpecTable
                hasImportedData={importedData != null}
                onReset={() => {
                  setImportedData(null);
                  setIsDirty(false);
                }}
                onValidated={(pnsTree) => {
                  setIsDirty(true);
                  setImportedData(pnsTree);
                }}
              />
            }
          />
        </div>
        <div className={`d-flex ${displayFullScreen === true || viewerMode === true ? "w-100" : "w-50"} pl-2`}>
          {nodes?.length > 0 && (
            <ParentChildChart
              fullScreen={displayFullScreen}
              onToggleFullScreen={() => setDisplayFullScreen(displayFullScreen === false)}
              items={nodes}
              viewerMode={viewerMode}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default PNS;
